// src/components/theme/elements/ThemeImageIndicator.js

import React, { useState, useEffect, useRef } from 'react';
import { Group, Image as KonvaImage, Rect as KonvaRect } from 'react-konva';
import PropTypes from 'prop-types';
import Konva from 'konva';
import { toast } from 'react-toastify';
import { hexToRgb, initializeColorReplaceFilter } from '../ThemeColorTools'; // Adjust the import path
import CustomColorPicker from '../CustomColorPicker';
import styles from './styles.module.css';
import { clamp, snapToGridFn, boundDrag } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';

/**
 * USAGES Array
 * Defines the different usages and their corresponding labels.
 */
const USAGES = [
  { id: 0, name: 'alert', label: 'ALERT' },
  { id: 1, name: 'special', label: 'SPECIAL' },
  { id: 2, name: 'turn_left', label: 'TURN LEFT' },
  { id: 3, name: 'turn_right', label: 'TURN RIGHT' },
  { id: 4, name: 'brake', label: 'BRAKE' },
  { id: 5, name: 'light', label: 'LIGHT' },
  { id: 6, name: 'temperature_warning', label: 'TEMPERATURE WARNING' },
  { id: 7, name: 'lock', label: 'LOCK' },
  { id: 8, name: 'foot_start', label: 'FOOT START' },
  { id: 9, name: 'humidity', label: 'HUMIDITY' },
  { id: 10, name: 'bluetooth', label: 'BLUETOOTH' },
];

/**
 * IMAGE_USAGE_OPTIONS Object
 * Maps usage names to their corresponding image paths.
 */
const IMAGE_USAGE_OPTIONS = {
  alert: './images_theme/alert-octagon.png',
  special: './images_theme/alien.png',
  turn_left: './images_theme/arrow-left-bold.png',
  turn_right: './images_theme/arrow-right-bold.png',
  brake: './images_theme/car-brake-alert.png',
  light: './images_theme/car-light-high.png',
  temperature_warning: './images_theme/fire.png',
  lock: './images_theme/lock.png',
  foot_start: './images_theme/shoe-sneaker.png',
  humidity: './images_theme/water.png',
  bluetooth: './images_theme/bluetooth.png',
};


/**
 * ThemeImageIndicatorSettings Component
 * Provides UI controls to configure the ThemeImageIndicator element.
 */
export const ThemeImageIndicatorSettings = ({ element, updateElement }) => {


  /**
   * Handler for usage selection change.
   * Updates the usage and enforces constraints based on the selected usage.
   */
  const handleUsageChange = (e) => {
    const id = parseInt(e.target.value, 10);
    const usage = USAGES.find((u) => u.id === id);
    if (usage) {
      updateElement(element.id, 'usage', id);
      // If the new usage is 'alert', set colorEnabled to default and disable its configurability
      if (usage.name === 'alert') {
        updateElement(element.id, 'colorEnabled', '#ffffff');
      } else {
        // If switching away from 'alert', set colorEnabled to default if not set
        if (!element.colorEnabled) {
          updateElement(element.id, 'colorEnabled', '#ffffff');
        }
      }
    } else {
      toast.error('Invalid usage selected.');
    }
  };

  /**
   * Handler for X position change.
   * Updates the X position of the element.
   */
  const handleXChange = (e) => {
    const x = parseInt(e.target.value, 10);
    if (!isNaN(x) && x >= 0) {
      updateElement(element.id, 'x', x);
    }
  };

  /**
   * Handler for Y position change.
   * Updates the Y position of the element.
   */
  const handleYChange = (e) => {
    const y = parseInt(e.target.value, 10);
    if (!isNaN(y) && y >= 0) {
      updateElement(element.id, 'y', y);
    }
  };


  /**
   * Handler to toggle the enabled state of the element.
   */
  const handleToggleEnabled = () => {
    updateElement(element.id, 'isEnabled', !element.isEnabled);
  };

  // Determine if the enabled color should be disabled in the UI based on usage
  const usage = USAGES.find((u) => u.id === element.usage);
  const isAlertUsage = usage && usage.name === 'alert';

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {/* Usage Selection */}
      <label>
        Usage:
        <select
          className={styles.select200}
          value={element.usage}
          onChange={handleUsageChange}
          style={{ marginLeft: '10px' }}
          aria-label="Set Usage"
        >
          {USAGES.map((usageOption) => (
            <option key={usageOption.id} value={usageOption.id}>
              {usageOption.label}
            </option>
          ))}
        </select>
      </label>

      {/* X Position */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        X Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_WIDTH - 1}
          value={element.x}
          onChange={handleXChange}
          style={{ marginLeft: '10px' }}
          aria-label="Set X Position"
        />
      </label>

      {/* Y Position */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Y Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_HEIGHT - 1}
          value={element.y}
          onChange={handleYChange}
          style={{ marginLeft: '10px' }}
          aria-label="Set Y Position"
        />
      </label>

      {/* Enabled color */}
      <CustomColorPicker
        label="Enabled color"
        color={element.colorEnabled}
        onChange={(value) => { updateElement(element.id, 'colorEnabled', value); }}
      />

      {/* Disabled color */}
      <CustomColorPicker
        label="Disabled color"
        color={element.colorDisabled}
        onChange={(value) => { updateElement(element.id, 'colorDisabled', value); }}
      />


      {/* Toggle Enabled State */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        State:
        <button
          onClick={handleToggleEnabled}
          style={{
            marginLeft: '10px',
            padding: '6px 12px',
            borderRadius: '4px',
            border: 'none',
            background: element.isEnabled ? '#dc3545' : '#28a745',
            color: '#ffffff',
            cursor: 'pointer',
          }}
          aria-label="Toggle Enabled State"
        >
          {element.isEnabled ? 'Disable' : 'Enable'}
        </button>
      </label>

    </div>
  );
};

ThemeImageIndicatorSettings.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    usage: PropTypes.number.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    colorEnabled: PropTypes.string, // Enabled color (optional)
    colorDisabled: PropTypes.string, // Disabled color (optional)
    isEnabled: PropTypes.bool, // Enabled state (optional)
  }).isRequired,
  updateElement: PropTypes.func.isRequired,
};



/////////////////////////////////////////////////////////
const ThemeImageIndicator = ({
  element,
  selectedElement,
  setSelectedElement,
  updateElement,
  render,editAllowed,
  scale,
  snapToGridEnabled,
  gridSize
}) => {
  const [imageObj, setImageObj] = useState(null);
  const imageRef = useRef();

  // Fixed dimensions for the image
  const fixedWidth = 24;
  const fixedHeight = 24;

  // Determine if this element is selected
  const isSelected = selectedElement && selectedElement.id === element.id;

  /**
   * Load the image based on the usage.
   * Sets the image object once loaded.
   */
  useEffect(() => {
    initializeColorReplaceFilter();
    // Find the usage name based on the usage ID
    const usage = USAGES.find((u) => u.id === element.usage);
    const usageName = usage ? usage.name : 'alien'; // Default to 'alien' if not found

    const img = new window.Image();
    img.src = IMAGE_USAGE_OPTIONS[usageName] || './images_theme/alien.png'; // Default image
    img.crossOrigin = 'Anonymous'; // To handle cross-origin issues if any
    img.onload = () => {
      setImageObj(img);
      console.log('Image loaded successfully.', img);
    };
    img.onerror = () => {
      toast.error('Failed to load image.');
    };
  }, [element.usage]);

  /**
   * Cache the image and redraw the layer when imageObj or colors change.
   */
  useEffect(() => {
    if (imageObj && imageRef.current) {
      imageRef.current.cache();
      imageRef.current.getLayer().batchDraw();
    }
  }, [imageObj, element.colorEnabled, element.colorDisabled, element.isEnabled]);

  // Handle drag end
  const handleDragEnd = (e) => {
    const { x, y } = e.target.position();

    // Ensure x and y are numbers
    const newX = typeof x === 'number' ? Math.round(x) : element.x;
    const newY = typeof y === 'number' ? Math.round(y) : element.y;

    updateElement(element.id, 'x', newX);
    updateElement(element.id, 'y', newY);
  };

  /**
   * Re-apply cache and redraw when tintColor changes.
   */
  useEffect(() => {
    if (imageRef.current) {
      imageRef.current.cache();
      imageRef.current.getLayer().batchDraw();
    }
  }, [element.colorEnabled, element.colorDisabled, element.isEnabled]);

  /**
   * Determine the current color based on the isEnabled state.
   */
  const currentColor = element.isEnabled
    ? element.colorEnabled || '#ffffff'
    : element.colorDisabled || '#9b9b9b';

  return (
    <Group
      x={element.x}
      y={element.y}
      draggable={!render && editAllowed} // Make draggable only when render is false
      dragBoundFunc={(pos) => { return boundDrag(pos, scale, snapToGridEnabled, gridSize, SCREEN_WIDTH, SCREEN_HEIGHT, fixedWidth, fixedHeight) }}
      onClick={() => setSelectedElement(element)}
      onTap={() => setSelectedElement(element)}
      onDragEnd={handleDragEnd}
      aria-label="Image Indicator"
    >
      {imageObj && (
        <KonvaImage
          image={imageObj}
          width={fixedWidth} // Set fixed width
          height={fixedHeight} // Set fixed height
          filters={[Konva.Filters.ColorReplaceFilter]} // Use the custom filter
          tintColor={hexToRgb(currentColor)} // Pass the current color
          ref={imageRef}
          onLoad={() => {
            if (imageRef.current) {
              imageRef.current.cache();
              imageRef.current.getLayer().batchDraw();
            }
          }}
        />
      )}
      {/* Add the debug blue rectangle */}
      {!render && (
        <KonvaRect
          x={0}
          y={0}
          width={fixedWidth}
          height={fixedHeight}
          stroke="#1976d2"
          strokeWidth={0.5}
          dash={isSelected ? [1, 1] : [0, 0]}
          listening={false} // Ensure it doesn't capture pointer events
        />
      )}
    </Group>
  );
};

ThemeImageIndicator.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    usage: PropTypes.number.isRequired,
    colorEnabled: PropTypes.string, // Enabled color (optional)
    colorDisabled: PropTypes.string, // Disabled color (optional)
    isEnabled: PropTypes.bool, // Enabled state (optional)
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }).isRequired,
  selectedElement: PropTypes.shape({
    id: PropTypes.string.isRequired,
    // ... other properties as needed
  }),
  setSelectedElement: PropTypes.func.isRequired,
  updateElement: PropTypes.func.isRequired,
  render: PropTypes.bool.isRequired, // Determines if the component is in render mode
  scale: PropTypes.number.isRequired, // Added as required
  snapToGridEnabled: PropTypes.bool.isRequired, // Added as required
  gridSize: PropTypes.number.isRequired, // Added as required
};

export default ThemeImageIndicator;